import { Checkbox } from '@repo/ui/components/checkbox';
import { Label } from '@repo/ui/components/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/components/select';
import { TableBody, TableHead, TableHeader, TableRow } from '@repo/ui/components/table';
import { type ReactNode, useId, useMemo } from 'react';

import { isForbidden, isNotFound } from '@/api/failure';

import { DayPicker } from '../back-office/DayPicker';
import {
  EmptyState,
  LoadFailed,
  ScreenFact,
  ScreenTable,
  TableNotice,
  TableSkeleton,
} from '../back-office/Screen';
import { formatBusinessDate, fromCalendarDate, toCalendarDate } from '../tickets/business-date';
import { STOCK_NOT_APPLICABLE } from './item-activity';
import { EVERY_OUTLET, type ItemReport } from './use-item-report';

/**
 * The controls and the table the two item reports share. The state they draw
 * is `use-item-report.ts`'s; the cells are `item-cells.tsx`'s.
 */

/** The margin of an item report: outlet, period, breakdown, and what the count means. */
export function ItemReportControls<Row>({ report }: { report: ItemReport<Row> }) {
  const { outlets, period, setPeriod, today, problem, byDay, setByDay, state } = report;

  // Base UI resolves what a trigger shows from `items` and not from the rendered
  // options, so a picker without it shows a bare UUID where a name belongs.
  // Derived from the same rows the options render.
  const outletItems = useMemo(
    () => [
      { label: 'Every outlet in reach', value: EVERY_OUTLET },
      ...outlets.map((outlet) => ({ label: outlet.name, value: outlet.id })),
    ],
    [outlets],
  );

  const id = useId();
  const byDayId = `${id}-by-day`;

  const loadedOutlet = state.kind === 'loaded' ? state.outlet : null;

  return (
    <>
      <div className="space-y-2">
        <Label htmlFor={`${id}-outlet`} className="text-sm text-muted-foreground">
          Outlet
        </Label>
        <Select
          value={report.outletId}
          items={outletItems}
          onValueChange={(next) => {
            report.setOutletId(next ?? EVERY_OUTLET);
          }}
        >
          <SelectTrigger
            id={`${id}-outlet`}
            aria-label="Outlet"
            className="h-10 w-full bg-card font-normal"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectItem value={EVERY_OUTLET}>Every outlet in reach</SelectItem>
              {outlets.map((outlet) => (
                <SelectItem key={outlet.id} value={outlet.id}>
                  {outlet.name}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor={`${id}-from`} className="text-sm text-muted-foreground">
          From
        </Label>
        <DayPicker
          id={`${id}-from`}
          value={period.from}
          emptyLabel="Choose a day"
          format={formatBusinessDate}
          fromDate={fromCalendarDate}
          toDate={toCalendarDate}
          maxDay={today}
          onValueChange={(next) => {
            setPeriod((held) => ({ ...held, from: next }));
          }}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor={`${id}-to`} className="text-sm text-muted-foreground">
          To
        </Label>
        <DayPicker
          id={`${id}-to`}
          value={period.to}
          emptyLabel="Choose a day"
          format={formatBusinessDate}
          fromDate={fromCalendarDate}
          toDate={toCalendarDate}
          // The routes refuse a period ending after the park's current trading
          // day. The notice below still stands — the day rolls at midnight in
          // Kolkata and a screen left open overnight would otherwise bound
          // itself at yesterday.
          maxDay={today}
          onValueChange={(next) => {
            setPeriod((held) => ({ ...held, to: next }));
          }}
        />
      </div>

      {problem !== null && (
        <p role="status" className="text-sm text-destructive">
          {problem}
        </p>
      )}

      <div className="flex items-start gap-2.5">
        {/* Base UI's checkbox is a `button` with `role="checkbox"` rather than an
            `input`, so a `<label for>` would bind to nothing. `aria-labelledby`
            is what names it. */}
        <Checkbox
          aria-labelledby={byDayId}
          checked={byDay}
          onCheckedChange={(next) => {
            setByDay(next === true);
          }}
          className="mt-0.5"
        />
        <span id={byDayId} className="text-sm text-foreground">
          Show each business day
        </span>
      </div>

      <ScreenFact label="Period">
        {period.from === '' || period.to === '' ? (
          'Choose both days.'
        ) : (
          <>
            {formatBusinessDate(period.from)} to {formatBusinessDate(period.to)}
            <br />
            <span className="text-muted-foreground">
              Both days included, by the trading day each record fell in.
            </span>
          </>
        )}
      </ScreenFact>

      <ScreenFact label="Counting">
        {loadedOutlet !== null && !loadedOutlet.carriesStock ? (
          <>
            {/* One string in one element rather than a name interpolated beside
                loose text: the read publishes the reason once on the outlet, and
                this is the one sentence that carries it. */}
            <span>{`${loadedOutlet.outletName} carries no stock.`}</span>
            <br />
            <span className="text-muted-foreground">
              There is no shelf here to count, so every item reads {STOCK_NOT_APPLICABLE}.
            </span>
          </>
        ) : (
          <>
            The latest count in the period.
            <br />
            <span className="text-muted-foreground">
              A count is a level and not a movement, so the days do not add up to it.
            </span>
          </>
        )}
      </ScreenFact>
    </>
  );
}

/**
 * The table of an item report, in each of its states.
 *
 * `forbidden` is the screen's own because the reason for a refusal is: the
 * owner's read is refused for carrying cost, the manager's for a role.
 */
export function ItemReportTable<Row>({
  report,
  label,
  columns,
  forbidden,
  children,
}: {
  report: ItemReport<Row>;
  label: string;
  columns: readonly { readonly label: string; readonly numeric?: boolean }[];
  forbidden: { readonly title: string; readonly description: string };
  /** The loaded rows, drawn by the screen that knows their type. */
  children: (items: readonly Row[]) => ReactNode;
}) {
  const { state } = report;

  return (
    <ScreenTable label={label}>
      <TableHeader>
        <TableRow>
          {columns.map((column) => (
            <TableHead key={column.label} className={column.numeric === true ? 'text-right' : ''}>
              {column.label}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>

      {state.kind === 'loading' && <TableSkeleton columns={columns.length} rows={6} />}

      {state.kind === 'idle' && (
        <TableNotice columns={columns.length}>
          <EmptyState
            title="Choose a period to report on"
            description="Item activity is read over a range of trading days. Pick the two that bound it."
          />
        </TableNotice>
      )}

      {state.kind === 'failed' && (
        <TableNotice columns={columns.length}>
          {isForbidden(state.error) ? (
            <EmptyState title={forbidden.title} description={forbidden.description} />
          ) : isNotFound(state.error) ? (
            // The routes answer 404 for an outlet that does not exist and for one
            // outside the caller's reach, identically and on purpose. Guessing
            // which is precisely the disclosure they declined to make.
            <EmptyState
              title="That outlet is not one this account can report on"
              description="It may have been removed, or it may be outside what this account reaches. Choose another outlet."
            />
          ) : (
            <LoadFailed
              error={state.error}
              onRetry={report.retry}
              title="This report could not be read"
            />
          )}
        </TableNotice>
      )}

      {state.kind === 'loaded' &&
        (state.items.length === 0 ? (
          <TableNotice columns={columns.length}>
            <EmptyState
              title="No item traded in this period"
              description="Nothing was sold, refunded, delivered, written off or counted between these two days. Widen the period, or choose another outlet."
            />
          </TableNotice>
        ) : (
          <TableBody>{children(state.items)}</TableBody>
        ))}
    </ScreenTable>
  );
}
